// src/pages/Admin/Adherents/AdherentCompetitions.jsx
import React, { useEffect, useMemo, useState } from "react";
import { getEventsByAdherent } from "../../../services/eventServices";
import { toAbsoluteEventPhotoUrl } from "../../../services/eventPhotoServices";


export default function AdherentCompetitions({ adherentId }) {
  const [competitions, setCompetitions] = useState([]);
  const [loading, setLoading] = useState(false);

    const toAbsolute = (u) => {
    if (!u) return "";
    const s = String(u).trim();
    // déjà absolu, data URL ou blob
    if (/^https?:\/\//i.test(s) || s.startsWith("data:") || s.startsWith("blob:")) return s;
    // chemins d'assets front (ex: /img/...): on laisse tel quel
    if (!s.startsWith("/uploads")) return s;
    // fichiers uploadés côté API
    const base = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";
    return `${base}${s}`;
  };

  useEffect(() => {
    if (!adherentId) return;
    (async () => {
      try {
        setLoading(true);
        const data = await getEventsByAdherent(adherentId);
        const arr = Array.isArray(data?.Events)
          ? data.Events
          : Array.isArray(data)
          ? data
          : [];
        setCompetitions(arr);
      } catch (error) {
        console.error("Erreur lors de la récupération des compétitions:", error);
      } finally {
        setLoading(false);
      }
    })();
  }, [adherentId]);

  const rows = useMemo(() => {
    return [...competitions].sort((a, b) => {
      const da = new Date(a?.start || 0).getTime();
      const db = new Date(b?.start || 0).getTime();
      return db - da; // récent -> ancien
    });
  }, [competitions]);

  const formatDate = (d) =>
    d
      ? new Date(d).toLocaleDateString("fr-FR", {
          weekday: "short",
          day: "2-digit",
          month: "long",
          year: "numeric",
        })
      : "—";

  const stripHtml = (html = "") => {
    try {
      const tmp = document.createElement("div");
      tmp.innerHTML = html;
      return tmp.textContent || tmp.innerText || "";
    } catch {
      return html;
    }
  };

  const badge = (text, classes) => (
    <span
      className={`inline-flex items-center rounded-md px-2.5 py-1 text-[11px] font-semibold ring-1 ${classes}`}
    >
      {text}
    </span>
  );

  const validateBadge = (ok) =>
    ok
      ? "bg-emerald-100 text-emerald-800 ring-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-200 dark:ring-emerald-800/50"
      : "bg-amber-100 text-amber-800 ring-amber-200 dark:bg-amber-900/40 dark:text-amber-200 dark:ring-amber-800/50";

  const classementBadge = (classement) => {
    if (classement === 1 || String(classement) === "1")
      return "bg-yellow-100 text-yellow-900 ring-yellow-200 dark:bg-yellow-900/40 dark:text-yellow-200 dark:ring-yellow-800/50";
    if (classement === 2 || String(classement) === "2")
      return "bg-slate-200 text-slate-800 ring-slate-300 dark:bg-slate-700/40 dark:text-slate-100 dark:ring-slate-600/50";
    if (classement === 3 || String(classement) === "3")
      return "bg-orange-100 text-orange-900 ring-orange-200 dark:bg-orange-900/40 dark:text-orange-200 dark:ring-orange-800/50";
    return "bg-slate-100 text-slate-800 ring-slate-200 dark:bg-slate-900/40 dark:text-slate-100 dark:ring-slate-800/50";
  };

  return (
    <section className="rounded-2xl overflow-hidden ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/90 dark:bg-slate-900/60 backdrop-blur">
      {/* En-tête harmonisé */}
      <div className="px-5 pt-5 sm:px-6">
        <h3 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-slate-100">
          Inscriptions aux compétitions
        </h3>
        <div className="mt-2 h-1 w-28 rounded-full bg-gradient-to-r from-violet-500 via-fuchsia-500 to-pink-500" />
      </div>

      {/* Contenu */}
      <div className="px-5 pb-6 sm:px-6 sm:pb-6 mt-4">
        {loading ? (
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div
                key={i}
                className="animate-pulse rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 p-5 bg-white/60 dark:bg-slate-900/40 h-28"
              />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <div className="rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 p-5 bg-slate-50 dark:bg-slate-900/40 text-sm text-slate-700 dark:text-slate-300">
            Aucune inscription aux compétitions.
          </div>
        ) : (
          <div className="space-y-3">
            {rows.map((c) => {
              const ae = c?.AdherentEvent || {};
              const date = formatDate(c?.start);
              const titre = c?.titre || "Sans titre";
              const lieu = c?.lieu_event || c?.lieu || c?.localisation || "";
              const horaire = stripHtml(c?.horaire_event || "");
              const img = c?.photo_url;
              const agenda = c?.agenda_url;
              const type = c?.type_event;
              const level = c?.level_event;
              const categorie = c?.cate_event;

              const valid = !!ae?.is_validate;
              const resultats = ae?.resultats;
              const classement = ae?.classement;
              const observations = ae?.observations;

              return (
                <div
                  key={ae?.event_id || `${titre}-${c?.start}`}
                  className="rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 p-4 bg-slate-50 dark:bg-slate-900/40"
                >
                  {/* Ligne 1 : miniature + en-tête + badges principaux */}
                  <div className="flex flex-col sm:flex-row gap-4">
                    {/* Affiche / photo */}
                    <div className="sm:w-40 w-full">
                      <div className="overflow-hidden rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 bg-white/70 dark:bg-slate-900/30">
                        {img ? (
                          <img
                            src={toAbsoluteEventPhotoUrl(img)}
                            alt={titre}
                            className="w-full h-28 object-cover"
                          />
                        ) : (
                          <div className="w-full h-28 grid place-items-center text-xs text-slate-500">
                            Pas d’image
                          </div>
                        )}
                      </div>
                      {agenda && (
                        <a
                          href={agenda}
                          target="_blank"
                          rel="noreferrer"
                          className="mt-2 inline-flex w-full items-center justify-center rounded-lg px-3 py-2 text-xs font-medium bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm"
                          title="Voir l’événement (agenda)"
                        >
                          Ouvrir l’événement
                        </a>
                      )}
                    </div>

                    {/* Détails titre/date/lieu + badges */}
                    <div className="flex-1">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                        <div>
                          <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                            {titre}
                          </div>
                          <div className="text-xs text-slate-600 dark:text-slate-300">
                            {date}
                            {lieu ? ` • ${lieu}` : ""}
                            {horaire ? ` • ${horaire}` : ""}
                          </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-2">
                          {type &&
                            badge(
                              type,
                              "bg-sky-100 text-sky-800 ring-sky-200 dark:bg-sky-900/40 dark:text-sky-200 dark:ring-sky-800/50"
                            )}
                          {level &&
                            badge(
                              level,
                              "bg-fuchsia-100 text-fuchsia-800 ring-fuchsia-200 dark:bg-fuchsia-900/40 dark:text-fuchsia-200 dark:ring-fuchsia-800/50"
                            )}
                          {categorie &&
                            badge(
                              categorie,
                              "bg-emerald-100 text-emerald-800 ring-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-200 dark:ring-emerald-800/50"
                            )}
                        </div>
                      </div>

                      {/* Validation + classement */}
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        {badge(valid ? "Validé" : "Non validé", validateBadge(valid))}
                        {classement && badge(`Classement : ${classement}`, classementBadge(classement))}
                      </div>
                    </div>
                  </div>

                  {/* Ligne 2 : blocs Résultats / Observations */}
                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                    {resultats && (
                      <div className="rounded-lg ring-1 ring-slate-200 dark:ring-slate-800 bg-white/80 dark:bg-slate-900/30 px-3 py-2">
                        <div className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
                          Résultats
                        </div>
                        <div className="text-slate-800 dark:text-slate-100">{resultats}</div>
                      </div>
                    )}

                    {observations && (
                      <div className="rounded-lg ring-1 ring-slate-200 dark:ring-slate-800 bg-white/80 dark:bg-slate-900/30 px-3 py-2">
                        <div className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
                          Observations
                        </div>
                        <div className="text-slate-800 dark:text-slate-100">{observations}</div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
